<template>
  <div class="core-page" v-loading="loading">
    <el-form size="small" inline>
      <el-form-item label="统计时间">
        <el-radio-group v-model="quick" @change="applyQuick">
          <el-radio-button :label="7">近 7 天</el-radio-button>
          <el-radio-button :label="30">近 30 天</el-radio-button>
          <el-radio-button :label="90">近 90 天</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item>
        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" unlink-panels range-separator="至"
          start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false" @change="onDateChange" />
      </el-form-item>
      <el-form-item label="汇总">
        <el-radio-group v-model="grain" @change="onGrainChange">
          <el-radio-button label="day">按日</el-radio-button>
          <el-radio-button label="week">按周</el-radio-button>
          <el-radio-button label="month">按月</el-radio-button>
          <el-radio-button label="year">按年</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="query">查询</el-button>
      </el-form-item>
      <el-form-item>
        <ai-insight ref="insight" />
      </el-form-item>
    </el-form>

    <p class="tip">数据来自每日核心指标。活跃是当天登录的乘客，下单是当天创建的订单，成交人数和营业额按乘车日统计已成交订单。按周、按月、按年时，活跃和下单是该周期内每天相加，同一个人多天登录会重复计入；VIP
      取该周期最后一天的存量。缺记录的周期在图上断开。</p>

    <el-row :gutter="12" class="cards">
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatNum(summary.avgActive, 1) }}</div>
          <div class="label">日均活跃</div>
          <div class="change">环比 <span :class="changeClass(mom.avgActive)">{{ formatChange(mom.avgActive) }}</span>　同比
            <span :class="changeClass(yoy.avgActive)">{{ formatChange(yoy.avgActive) }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatNum(summary.avgOrder, 1) }}</div>
          <div class="label">日均下单</div>
          <div class="change">环比 <span :class="changeClass(mom.avgOrder)">{{ formatChange(mom.avgOrder) }}</span>　同比
            <span :class="changeClass(yoy.avgOrder)">{{ formatChange(yoy.avgOrder) }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ summary.finishOrderNum || 0 }}</div>
          <div class="label">成交人数</div>
          <div class="change">环比 <span :class="changeClass(mom.finishOrderNum)">{{ formatChange(mom.finishOrderNum)
          }}</span> 同比 <span :class="changeClass(yoy.finishOrderNum)">{{ formatChange(yoy.finishOrderNum) }}</span>
          </div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatMoney(summary.totalAmount) }}</div>
          <div class="label">营业额</div>
          <div class="change">环比 <span :class="changeClass(mom.totalAmount)">{{ formatChange(mom.totalAmount)
          }}</span> 同比 <span :class="changeClass(yoy.totalAmount)">{{ formatChange(yoy.totalAmount) }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatRate(summary.orderRate) }}</div>
          <div class="label">下单率</div>
          <div class="change">下单数 / 活跃人数 环比 <span :class="changeClass(mom.orderRate)">{{ formatChange(mom.orderRate)
          }}</span></div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ summary.latestVip == null ? "—" : summary.latestVip }}</div>
          <div class="label">最新 VIP</div>
          <div class="change">较期初 <span :class="changeClass(summary.vipChange)">{{ formatDelta(summary.vipChange)
          }}</span>　人均 {{ formatMoney(summary.amountPerPassenger) }}</div>
        </el-card>
      </el-col>
    </el-row>
    <p class="tip">这段时间有 {{ summary.recordedDays || 0 }} 天记录。环比是紧挨着的上一个同样天数，同比是去年同一段日期。</p>

    <div class="block-title">活跃、下单、成交、营业额和 VIP</div>
    <chart ref="trendChart" :height="360" />

    <div class="block-title">{{ detailTitle }}</div>
    <el-table :data="tableDays" stripe max-height="480" empty-text="这段时间还没有核心指标">
      <el-table-column prop="period" label="周期" min-width="180" fixed />
      <el-table-column label="VIP" width="100">
        <template slot-scope="scope">{{ formatCount(scope.row.vipNum) }}</template>
      </el-table-column>
      <el-table-column label="活跃" width="100">
        <template slot-scope="scope">{{ formatCount(scope.row.activeNum) }}</template>
      </el-table-column>
      <el-table-column label="下单" width="100">
        <template slot-scope="scope">{{ formatCount(scope.row.orderNum) }}</template>
      </el-table-column>
      <el-table-column label="成交人数" width="110">
        <template slot-scope="scope">{{ formatCount(scope.row.finishOrderNum) }}</template>
      </el-table-column>
      <el-table-column label="营业额" min-width="120">
        <template slot-scope="scope">{{ formatMoney(scope.row.totalAmount) }}</template>
      </el-table-column>
      <el-table-column prop="recordedDays" label="记录天数" width="100" />
    </el-table>
  </div>
</template>

<script>
import { getDauAnalysis } from "@/api/data";
import { DateUtil } from "@/util";
import AiInsight from "@/components/AiInsight";

function rangeEndingYesterday(days) {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return [DateUtil.format(start, "yyyy-MM-dd"), DateUtil.format(end, "yyyy-MM-dd")];
}

function yuan(value) {
  if (value == null || value === "") {
    return null;
  }
  return Math.round(Number(value)) / 100;
}

function formatYuan(value) {
  if (value == null || value === "") {
    return "—";
  }
  const number = Number(value);
  const sign = number < 0 ? "-" : "";
  const abs = Math.abs(number);
  if (abs >= 10000) {
    return `${sign}${(abs / 10000).toFixed(2)}万`;
  }
  return sign + abs.toLocaleString("zh-CN", { maximumFractionDigits: 0 });
}

export default {
  components: { AiInsight },
  data() {
    return {
      quick: 30,
      grain: "day",
      dateRange: rangeEndingYesterday(30),
      loading: false,
      summary: {},
      mom: {},
      yoy: {},
      days: []
    };
  },
  computed: {
    detailTitle() {
      if (this.grain === "week") {
        return "每周明细";
      }
      if (this.grain === "month") {
        return "每月明细";
      }
      if (this.grain === "year") {
        return "每年明细";
      }
      return "每日明细";
    },
    tableDays() {
      return this.days.filter((item) => item.activeNum != null || item.orderNum != null || item.finishOrderNum != null);
    }
  },
  created() {
    this.query();
  },
  methods: {
    formatNum(value, precision) {
      if (value == null || value === "") {
        return "—";
      }
      return Number(value).toFixed(precision);
    },
    formatCount(value) {
      if (value == null || value === "") {
        return "—";
      }
      return value;
    },
    formatRate(value) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatChange(value) {
      if (value == null || value === "") {
        return "—";
      }
      const num = Number(value);
      const sign = num > 0 ? "+" : "";
      return `${sign}${num.toFixed(1)}%`;
    },
    changeClass(value) {
      if (value == null || value === "") {
        return "";
      }
      const num = Number(value);
      if (num > 0) {
        return "up";
      }
      if (num < 0) {
        return "down";
      }
      return "";
    },
    formatDelta(value) {
      if (value == null || value === "") {
        return "—";
      }
      const num = Number(value);
      return num > 0 ? `+${num}` : String(num);
    },
    formatMoney(value) {
      return formatYuan(yuan(value));
    },
    applyQuick(days) {
      if (!days) {
        return;
      }
      this.dateRange = rangeEndingYesterday(days);
      this.query();
    },
    onDateChange() {
      this.quick = "";
      this.query();
    },
    onGrainChange(grain) {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.query();
        return;
      }
      const end = new Date(this.dateRange[1].replace(/-/g, "/"));
      let start;
      if (grain === "year") {
        start = new Date(end.getFullYear() - 2, 0, 1);
      } else if (grain === "month") {
        start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
      } else if (grain === "week") {
        start = new Date(end);
        start.setDate(start.getDate() - 83);
      } else {
        this.query();
        return;
      }
      this.quick = "";
      this.dateRange = [DateUtil.format(start, "yyyy-MM-dd"), DateUtil.format(end, "yyyy-MM-dd")];
      this.query();
    },
    query() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning("请选择日期");
        return;
      }
      this.loading = true;
      getDauAnalysis({ startDate: this.dateRange[0], endDate: this.dateRange[1], grain: this.grain }).then((res) => {
        const data = res.data || {};
        this.summary = data.summary || {};
        this.mom = data.mom || {};
        this.yoy = data.yoy || {};
        this.days = data.periods || [];
        this.renderTrend(this.days);
        this.$nextTick(() => {
          if (!this.$refs.insight) {
            return;
          }
          this.$refs.insight.setContext("core", {
            grain: this.grain,
            dateRange: this.dateRange,
            summary: this.summary,
            mom: this.mom,
            yoy: this.yoy,
            periods: this.days.filter((item) => item.recordedDays).map((item) => ({
              period: item.period,
              vipNum: item.vipNum,
              activeNum: item.activeNum,
              orderNum: item.orderNum,
              finishOrderNum: item.finishOrderNum,
              totalAmount: item.totalAmount,
              recordedDays: item.recordedDays
            }))
          });
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    renderTrend(days) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const lines = [params[0].axisValue];
            params.forEach((item) => {
              const text = item.seriesName === "营业额" ? formatYuan(item.data) : (item.data == null ? "—" : item.data);
              lines.push(`${item.marker}${item.seriesName}：${text}`);
            });
            return lines.join("<br/>");
          }
        },
        legend: { data: ["活跃", "下单", "成交人数", "营业额", "VIP"] },
        grid: { left: 56, right: 120, bottom: days.length > 14 ? 64 : 32, top: 48 },
        xAxis: {
          type: "category",
          data: days.map((item) => item.period),
          axisLabel: { rotate: days.length > 14 ? 40 : 0 }
        },
        yAxis: [
          { type: "value", name: "人数 / 单数" },
          { type: "value", name: "营业额", axisLabel: { formatter: (value) => formatYuan(value) } },
          { type: "value", name: "VIP", position: "right", offset: 64, scale: true }
        ],
        series: [
          { name: "活跃", type: "line", data: days.map((item) => item.activeNum) },
          { name: "下单", type: "line", data: days.map((item) => item.orderNum) },
          { name: "成交人数", type: "line", data: days.map((item) => item.finishOrderNum) },
          { name: "营业额", type: "line", yAxisIndex: 1, data: days.map((item) => yuan(item.totalAmount)) },
          { name: "VIP", type: "line", yAxisIndex: 2, data: days.map((item) => item.vipNum) }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.trendChart) {
          this.$refs.trendChart.setOption(option, true);
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.tip {
  color: #909399;
  font-size: 13px;
  line-height: 22px;
  margin: 0 0 12px;
}

.cards {
  margin-bottom: 4px;
}

.num {
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.label {
  color: #606266;
  font-size: 13px;
}

.change {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.block-title {
  margin: 18px 0 8px;
  font-size: 15px;
  font-weight: 600;
}
</style>
